export interface ActiveFilter {
  /** The underlying filter/query-param key (e.g. `status`) — passed back to `onRemove`. */
  key: string;
  /** The rendered chip label (e.g. `Status: Active`). */
  label: string;
}

interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

/** A single removable "active filter" chip (#13136). */
export function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter ${label}`}
        className="text-indigo-400 hover:text-indigo-700"
      >
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

interface FilterBarProps {
  filters: ActiveFilter[];
  onRemove: (key: string) => void;
  onClearAll?: () => void;
  className?: string;
  /** BR-MOB-002 (#22193): below 768px render the chips as ONE horizontally scrolling row under
   *  the search box instead of wrapping. Opt-in; default off, so existing callers are unchanged. */
  strip?: boolean;
}

/**
 * Renders the current set of active list filters as removable chips, with a
 * "N filters · Clear all" summary (#13136). Renders nothing when there are no
 * active filters — a caller composing this into `DataListShell` doesn't need to
 * conditionally mount it.
 */
export function FilterBar({ filters, onRemove, onClearAll, className = '', strip = false }: FilterBarProps) {
  if (filters.length === 0) return null;

  const stripClass = strip
    ? ' max-md:flex-nowrap max-md:overflow-x-auto max-md:[scrollbar-width:none] max-md:[&>*]:shrink-0 max-md:[&>*]:whitespace-nowrap'
    : '';

  return (
    <div data-testid="filter-bar" className={`flex flex-wrap items-center gap-2${stripClass} ${className}`}>
      <span className="text-xs text-gray-500">
        {filters.length} filter{filters.length !== 1 ? 's' : ''}
      </span>
      {onClearAll && (
        <>
          <span aria-hidden="true" className="text-gray-300">·</span>
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            Clear all
          </button>
        </>
      )}
      {filters.map(f => (
        <FilterChip key={f.key} label={f.label} onRemove={() => onRemove(f.key)} />
      ))}
    </div>
  );
}
